<template>
  <form @submit.prevent="handleSubmit" id="edit_request_form" enctype="multipart/form-data">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading request details...</p>
    </div>

    <!-- Form Fields -->
    <div v-else>
      <!-- Request Title -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Request Title/Issue <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="edit_title" name="title" ref="title"
          v-model="form.title" placeholder="Request Title/Issue" />
      </div>

      <!-- Issue Related -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Issue Related <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.issue_related_to" name="issue_related_to" ref="related_to">
          <option value="" disabled>Select</option>
          <option value="food safety">Food Safety</option>
          <option value="breakdown">Breakdown</option>
          <option value="other">Other</option>
        </select>
      </div>

      <!-- Other Issue Detail -->
      <div class="mb-3" v-if="form.issue_related_to === 'other'">
        <label class="form-label form-label-thin">Other Issue <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="edit_other_issue_detail" name="other_issue_detail" ref="other_issue_detail"
          v-model="form.other_issue_detail" placeholder="Enter Detail of Other Issue" />
      </div>

      <!-- Priority -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Priority <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.priority" name="priority" ref="priority">
          <option value="" disabled>Select</option>
          <option value="low">🔵 Low</option>
          <option value="medium">🟡 Medium</option>
          <option value="high">🔴 High</option>
        </select>
      </div>

      <!-- Location -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Location <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.location" name="location" ref="location">
          <option value="" disabled>Select</option>
          <option v-for="location in locations" :key="location.id" :value="location.external_id">
            {{ location.name }}
          </option>
        </select>
      </div>

      <!-- Asset -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Asset (If Any)</label>
        <select class="form-select form-select-thin" v-model="form.asset" name="asset" ref="asset">
          <option value="" disabled>Select</option>
          <option v-for="asset in assets" :key="asset.id" :value="asset.external_id">
            {{ asset.name }}
          </option>
        </select>
      </div>

      <!-- Description -->
      <div class="mb-3">
        <label class="form-label form-label-thin">Description <span class="text-danger">*</span></label>
        <textarea class="form-control form-control-thin" id="edit_description" name="description" ref="description"
          v-model="form.description" placeholder="Request Description" rows="2"></textarea>
      </div>

      <!-- File Upload -->
      <div class="mb-4">
        <FileUploader v-model:files="form.files" maxFiles="3" 
          :help-text="'Upload vendor-related documents or images. Max 10 files.'" />
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-danger btn-sm hover-white" @click="close" :disabled="loading">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button type="submit" class="btn btn-primary text-light hover-white btn-md form-submit-btn" :disabled="submitting || loading">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
        <i v-else class="fa fa-save me-1"></i>
        {{ submitting ? 'Updating...' : 'Update Request' }}
      </button>
    </div>
  </form>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import FileUploader from '@/components/FileUploader.vue';

export default {
  name: 'EditRequest',
  emits: ['updated', 'close'],
  components: {
    FileUploader
  },
  data() {
    return {
      form: {
        title: '',
        asset: '',
        location: '',
        priority: '',
        issue_related_to: '',
        other_issue_detail: '',
        description: '',
        files: null
      },
      requestId: null,
      requestExternalId: null,
      submitting: false,
      loading: false,
      locations: [],
      assets: []
    }
  },
  mounted() {
    this.get_locations()
    this.get_assets()
  },
  methods: {
    async setRequestData(request) {
      this.requestId = request.id
      this.requestExternalId = request.external_id
      
      // Show loading state
      this.loading = true
      
      try {
        // Fetch request details using the show API
        const result = await form_api_submit({
          url: 'wo/request/show/' + request.id,
          method: 'GET',
          show_success_toast: false,
          show_error_toast: true
        })

        if (result.success) {
          const data = result.data.request
          this.form.title = data.title || ''
          this.form.issue_related_to = data.issue_related_to || ''
          this.form.other_issue_detail = data.other_issue_detail || ''
          this.form.priority = data.priority || ''
          this.form.location = data.get_location?.external_id || ''
          this.form.asset = data.get_asset?.external_id || ''
          this.form.description = data.description || ''
          this.form.files = null // Reset files
        }
      } catch (error) {
        console.error('Error fetching request details:', error)
        this.$toast('Failed to load request details', false)
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.requestId = null
      this.requestExternalId = null
      this.form = {
        title: '',
        asset: '',
        location: '',
        priority: '',
        issue_related_to: '',
        other_issue_detail: '',
        description: '',
        files: null
      }
    },

    async handleSubmit() {
      if (!this.requestId) {
        this.$toast('Invalid request ID', false)
        return
      }

      this.submitting = true

      try {
        const result = await form_api_submit({
          url: 'wo/request/update/' + this.requestId,
          method: 'POST',
          data: this.form,
          take_form_data: true,
          curr_form_id: '#edit_request_form',
        })

        if (result.success) {
          this.$emit('updated', result.data.request)
        //   this.$toast('Request updated successfully!', true)
          this.close()
          this.resetForm()
        }
      } catch (error) {
        console.error('Error updating request:', error)
        // this.$toast('Failed to update request', false)
      } finally {
        this.submitting = false
      }
    },

    async get_locations() {
      const result = await form_api_submit({
        url: 'locations/dropdown',
        method: 'GET',
        show_success_toast: false
      })
      if (result.success) {
        this.locations = result.data
      }
    },

    async get_assets() {
      const result = await form_api_submit({
        url: 'asset/dropdown',
        method: 'GET',
        show_success_toast: false
      })
      if (result.success) {
        this.assets = result.data
      }
    },

    close() {
      this.resetForm()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.form-label-thin {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.form-control-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-control-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-select-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-select-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

/* Spinner */
.spinner-border-sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}
</style>